import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { cn } from './cn';
import { InfoTip } from './InfoTip';
import { useDismissable, useEntitySearch, type EntitySearchPage } from './useEntitySearch';

/**
 * Choosing one record out of a table that is too big to be a dropdown.
 *
 * The rule this exists to enforce: a `<select>` is a list of everything, and
 * a list of everything stops working at exactly the size real customers
 * arrive with. The employee picker on "raise an examination" fetched the
 * first hundred workers by creation date — so on a company with more than a
 * hundred, the person you needed was not merely hard to find, they were
 * absent from the DOM and unreachable from that screen.
 *
 * So the contract here is deliberately narrow:
 *
 *   · **The server does the filtering.** `fetchOptions` is handed the search
 *     text and a cursor and returns a page. Nothing is filtered in the
 *     browser, because filtering in the browser requires having fetched
 *     everything, which is the thing being fixed.
 *
 *   · **`value` is the record, not its id.** An id alone cannot be rendered —
 *     the component would have to fetch the row back just to show a name, on
 *     every mount of every form. Holding the object means a selected value
 *     renders instantly and survives a remount, and callers that need the id
 *     write `value?.id`.
 *
 *   · **Nothing is requested until the query is worth running.** `minChars`
 *     defaults to 2. A single character against an employee search is an
 *     enumeration oracle, and it also returns a page nobody wanted.
 *
 * What it does NOT do: virtualise. Pages stay at 20–50 rows and there is a
 * "load more"; a combobox that renders a thousand options has recreated the
 * problem in a nicer font.
 */

/** Re-exported under its old name; the shape lives with the shared hook. */
export type EntityPage<T> = EntitySearchPage<T>;

export interface EntityComboboxProps<T> {
  value: T | null;
  onChange: (next: T | null) => void;
  /** Server-side page. `search` is already debounced and past `minChars`. */
  fetchOptions: (search: string, cursor: string | null) => Promise<EntityPage<T>>;
  getKey: (item: T) => string;
  /** The row inside the list — give people enough to tell two records apart. */
  renderOption: (item: T) => ReactNode;
  /** The closed state. Defaults to `renderOption`. */
  renderValue?: (item: T) => ReactNode;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Below this many characters nothing is requested. The empty query is the exception — see `initial`. */
  minChars?: number;
  /** How many rows to show when the picker opens with nothing typed. 0 keeps it blank until typed. */
  initial?: number;
  debounceMs?: number;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  /** Re-runs the search when these change — the parent of a cascade. */
  deps?: unknown[];
  emptyMessage?: string;
  /** Live feedback under the field (what was matched, …). */
  hint?: ReactNode;
  /** The standing explanation: an ⓘ beside the label, not a line under the field. */
  info?: ReactNode;
  error?: string | null;
  className?: string;
}

export function EntityCombobox<T>({
  value, onChange, fetchOptions, getKey, renderOption, renderValue,
  label, placeholder = 'Select…', searchPlaceholder = 'Search…',
  minChars = 2, initial = 5, debounceMs = 300, disabled, required, clearable = true,
  deps = [], emptyMessage, hint, info, error, className,
}: EntityComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const labelId = useId();

  // The debounce, the stale-response guard and the paging live in the shared
  // hook, so the multi-select cannot drift from this one.
  const s = useEntitySearch<T>({ fetchOptions, active: open, minChars, debounceMs, deps, initial });
  const rootRef = useDismissable(open, () => setOpen(false));

  const { items, loading, tooShort, ready, cursor, total } = s;
  const loadError = s.error;

  // A new result set: the keyboard cursor belongs at the top of it.
  useEffect(() => { setActive(0); }, [s.search]);

  // A cascade parent changed: whatever was chosen may no longer be valid.
  // Clearing is the safe direction — keeping a stale child selection is how a
  // worker from the previous plant ends up on the new plant's examination.
  // Compared with the previous values rather than skipping a "first" run:
  // StrictMode mounts effects twice in development, so a first-run flag was
  // spent on the throwaway mount and the real one cleared a value chosen
  // before mount — a booking preselected from the queue vanished.
  const prevDeps = useRef(deps);
  useEffect(() => {
    const changed = deps.length !== prevDeps.current.length
      || deps.some((d, i) => !Object.is(d, prevDeps.current[i]));
    prevDeps.current = deps;
    if (!changed) return;
    onChange(null);
    s.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function choose(item: T) {
    onChange(item);
    setOpen(false);
    s.reset();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(Math.max(items.length - 1, 0)); return; }
    if (e.key === 'Tab') { setOpen(false); return; }
    if (e.key === 'Enter' && items[active]) {
      e.preventDefault();
      choose(items[active]!);
    }
  }

  // The trigger itself answers the keyboard too: a picker that only opens on
  // a click is unreachable from a form somebody is tabbing through.
  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
    if ((e.key === 'Backspace' || e.key === 'Delete') && value && clearable && !disabled) {
      e.preventDefault();
      onChange(null);
    }
  }

  const show = renderValue ?? renderOption;

  return (
    <div className={cn('relative', className)} ref={rootRef}>
      {label && (
        <span className="mb-1.5 flex items-center gap-1">
          <span id={labelId} className="text-caption text-neutral-700">
            {label}
            {required && <span aria-hidden className="ml-0.5 text-danger-600">*</span>}
          </span>
          {info && <InfoTip label={`About ${label}`}>{info}</InfoTip>}
        </span>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={label ? labelId : undefined}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-input border px-3 text-left text-body',
          'transition-colors duration-hover focus:outline-none focus:ring-4 focus:ring-primary-500/15',
          error ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400 focus:border-primary-500',
          disabled && 'cursor-not-allowed bg-neutral-50 text-neutral-400',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !value && 'text-neutral-500')}>
          {value ? show(value) : placeholder}
        </span>

        {value && clearable && !disabled && (
          // A span, not a button: a button inside a button is invalid markup
          // and browsers resolve it by dropping one of them.
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onChange(null); }
            }}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X aria-hidden className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-neutral-400" />
      </button>

      {hint && !error && (
        <span className="mt-1.5 block text-caption font-normal text-neutral-500">{hint}</span>
      )}
      {error && <span className="mt-1.5 block text-caption text-danger-600">{error}</span>}

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[280px] overflow-hidden rounded-card border border-neutral-200 bg-white shadow-dropdown animate-fade-in">
          <div className="relative border-b border-neutral-100">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            />
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={items[active] ? `${listId}-${getKey(items[active]!)}` : undefined}
              value={s.search}
              onChange={(e) => s.setSearch(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder || 'Search'}
              className="h-10 w-full border-0 pl-9 pr-8 text-body text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
            />
            {loading && (
              <Loader2 aria-hidden className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-400" />
            )}
            {!loading && s.search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => { s.setSearch(''); inputRef.current?.focus(); }}
                className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
              >
                <X aria-hidden className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto py-1">
            {/* Each state says what is actually true. "No data" tells a person
                nothing about whether to keep typing. */}
            {!ready && (
              <li className="px-3 py-6 text-center text-small text-neutral-500">
                {tooShort
                  ? `Keep typing — at least ${minChars} characters.`
                  : searchPlaceholder}
              </li>
            )}

            {ready && loadError && (
              <li className="px-3 py-6 text-center text-small text-danger-600">
                {loadError}{' '}
                <button
                  type="button"
                  onClick={s.retry}
                  className="font-medium underline"
                >
                  Try again
                </button>
              </li>
            )}

            {ready && !loadError && loading && items.length === 0 && (
              <li className="px-3 py-6 text-center text-small text-neutral-500">Searching…</li>
            )}

            {ready && !loadError && !loading && items.length === 0 && (
              <li className="px-3 py-6 text-center text-small text-neutral-500">
                {s.browsing
                  ? (emptyMessage ?? 'Nothing here yet.')
                  : (emptyMessage ?? `Nothing matches “${s.search.trim()}”.`)}
              </li>
            )}

            {items.map((item, i) => {
              const key = getKey(item);
              const selected = value ? getKey(value) === key : false;
              return (
                <li key={key} id={`${listId}-${key}`} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => choose(item)}
                    onMouseEnter={() => setActive(i)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2 text-left text-small',
                      i === active ? 'bg-primary-50' : 'hover:bg-neutral-50',
                    )}
                  >
                    <Check
                      aria-hidden
                      className={cn('mt-0.5 h-4 w-4 shrink-0',
                        selected ? 'text-primary-600' : 'text-transparent')}
                    />
                    <span className="min-w-0 flex-1">{renderOption(item)}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Browsing the top few: say that it is the top few, and that
              typing finds the rest. Without this a five-row list reads as
              "there are five". */}
          {ready && s.browsing && !loading && items.length > 0 && (
            <div className="border-t border-neutral-100 px-3 py-1.5 text-center text-caption font-normal text-neutral-500">
              {s.truncated
                ? `Showing the first ${items.length}${total ? ` of ${total}` : ''} — type to search`
                : `${items.length} in total`}
            </div>
          )}

          {ready && cursor && !s.browsing && (
            <div className="border-t border-neutral-100 px-3 py-2 text-center">
              <button
                type="button"
                onClick={s.loadMore}
                disabled={loading}
                className="text-caption font-medium text-primary-600 hover:underline disabled:text-neutral-400"
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
              {total !== undefined && (
                <span className="ml-2 text-caption text-neutral-500">
                  showing {items.length} of {total}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
